import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../Auth";
import DateInput from "../components/datePicker/DateInput";

const EmployeeHealthExamination = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserList, setShowUserList] = useState(true);
  const [healthHistory, setHealthHistory] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState("");

  const positions = [
    "RN",
    "PT",
    "PTA",
    "OT",
    "OTA",
    "ST",
    "HHA",
    "LVN",
    "MSW",
    "Caregiver",
    "CNA",
    "Office Assistant",
    "ADMIN"
  ];

  const positionMap = {
    "RN": "Registered Nurse",
    "PT": "Physical Therapist",
    "PTA": "Physical Therapist Assistant",
    "OT": "Occupational Therapist",
    "OTA": "Occupational Therapist Assistant",
    "ST": "Speech Language Pathologist",
    "HHA": "Certified Home Health Aide",
    "LVN": "Licensed Vocational Nurse",
    "MSW": "Medical Social Worker",
    "ADMIN": "Administrator",
    "Office Assistant": "Office Assistant"
  };

  const [healthFormData, setHealthFormData] = useState({
    employeeId: "",
    employeeName: "",
    position: "",
    positionFull: "", // Full position name
    // Initial Physician's Statement
    patientTitle: "", // "Mr." or "Ms."
    patientName: "",
    conditionException: "",
    noHazardStatement: "",
    communicableDiseaseFree: "",
    // EXAMINATION
    height: "",
    weight: "",
    bloodPressure: "",
    pulse: "",
    // Family History
    familyHistory: [
      { member: "", illness: "", relationship: "" },
      { member: "", illness: "", relationship: "" },
      { member: "", illness: "", relationship: "" },
    ],
    // PHYSICAL EXAMINATION
    ppdTestResult: "", // "Negative" or "Positive"
    ppdMantoux: "",
    ppdDateAdministered: "",
    chestXRayResult: "", // "Negative" or "Positive"
    chestXRayDate: "",
    // Body Systems - Left Column
    adenopathy: "",
    reflexes: "",
    eyes: "",
    hearing: "",
    nose: "",
    throat: "",
    tongue: "",
    teeth: "",
    abdomen: "",
    rectal: "",
    // Body Systems - Right Column (Cardiopulmonary)
    chestBreathSounds: "",
    chestResonance: "",
    heartSize: "",
    heartMurmur: "",
    heartRhythm: "",
    arteries: "",
    // Physician's Information
    physicianName: "",
    physicianPhone: "",
    physicianAddress: "",
    physicianSignature: "",
    physicianDate: "",
  });

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      // Exclude file data - we only need user list for selection
      const data = await getUsers(true);
      setUsers(data);
      setLoading(false);
    };
    loadUsers();

    // Load health history from localStorage
    const savedHistory = localStorage.getItem("healthHistory");
    if (savedHistory) {
      setHealthHistory(JSON.parse(savedHistory));
    }
  }, []);

  const handleInputChange = (e) => {
    const { id, name, value, type, checked } = e.target;
    let fieldName = name || id;

    // If position is selected
    if (id === "selectedPosition") {
      setSelectedPosition(value);
      const positionFull = positionMap[value] || value;
      setHealthFormData(prev => ({
        ...prev,
        employeeId: "",
        employeeName: "",
        position: "",
        positionFull: positionFull
      }));
      return;
    }

    // If employee is selected, auto-populate name and position
    if (id === "employeeId") {
      const selectedUser = users.find(u => u._id === value);
      if (selectedUser) {
        const fullName = `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim();
        const empPosition = selectedUser.position || "";
        const empPositionFull = positionMap[empPosition] || empPosition;
        setHealthFormData(prev => ({
          ...prev,
          employeeId: value,
          employeeName: fullName,
          patientName: fullName,
          position: empPosition,
          positionFull: empPositionFull // Store full position name
        }));
        return;
      }
    }

    // Handle family history array
    if (fieldName.startsWith("familyMember_")) {
      const index = parseInt(fieldName.split("_")[1]);
      setHealthFormData(prev => {
        const newFamilyHistory = [...prev.familyHistory];
        newFamilyHistory[index] = { ...newFamilyHistory[index], member: value };
        return { ...prev, familyHistory: newFamilyHistory };
      });
      return;
    }
    if (fieldName.startsWith("familyIllness_")) {
      const index = parseInt(fieldName.split("_")[1]);
      setHealthFormData(prev => {
        const newFamilyHistory = [...prev.familyHistory];
        newFamilyHistory[index] = { ...newFamilyHistory[index], illness: value };
        return { ...prev, familyHistory: newFamilyHistory };
      });
      return;
    }
    if (fieldName.startsWith("familyRelationship_")) {
      const index = parseInt(fieldName.split("_")[1]);
      setHealthFormData(prev => {
        const newFamilyHistory = [...prev.familyHistory];
        newFamilyHistory[index] = { ...newFamilyHistory[index], relationship: value };
        return { ...prev, familyHistory: newFamilyHistory };
      });
      return;
    }

    setHealthFormData(prev => ({
      ...prev,
      [fieldName]: type === "checkbox" ? checked : value
    }));
  };

  // Function to fill all body system fields with WNL/Normal
  const fillAllNormal = (value = "WNL") => {
    setHealthFormData(prev => ({
      ...prev,
      adenopathy: value,
      reflexes: value,
      eyes: value,
      hearing: value,
      nose: value,
      throat: value,
      tongue: value,
      teeth: value,
      abdomen: value,
      rectal: value,
      chestBreathSounds: value,
      chestResonance: value,
      heartSize: value,
      heartMurmur: value === "WNL" ? "None" : value,
      heartRhythm: value === "WNL" ? "Regular" : value,
      arteries: value,
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const formatDateUS = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return "";
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "";
    try {
      let date;
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return "";
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month} / ${day} / ${year}`;
    } catch {
      return "";
    }
  };

  const generateHealthPrintFormHTML = (data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Employee Health Examination - ${data.employeeName || "Employee"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 8pt;
            line-height: 1.25;
            padding: 10px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 8px;
            padding-bottom: 8px;
            border-bottom: 3px solid #2c3e50;
            min-height: 90px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 15px;
          }
          .header h1 {
            font-size: 16pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .header .subtitle {
            font-size: 9pt;
            font-style: italic;
            color: #666;
            margin: 4px 0 0 0;
          }
          .contact-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 8pt;
            text-align: right;
            line-height: 1.4;
          }
          .physician-statement {
            margin-bottom: 8px;
            padding: 6px 0;
          }
          .statement-text {
            font-size: 8pt;
            line-height: 1.4;
            margin-bottom: 4px;
            color: #333;
          }
          .statement-line {
            border-bottom: 1px solid #333;
            min-height: 14px;
            display: inline-block;
            min-width: 100px;
            margin: 0 3px;
          }
          .statement-blank {
            border-bottom: 1px solid #333;
            min-height: 16px;
            display: block;
            margin-top: 2px;
            width: 100%;
            font-size: 8pt;
          }
          .divider {
            border-top: 2px solid #333;
            margin: 6px 0;
          }
          .section-title {
            font-size: 10pt;
            font-weight: bold;
            margin: 8px 0 6px 0;
            color: #2c3e50;
            text-transform: uppercase;
            text-align: center;
            letter-spacing: 0.5px;
          }
          .examination-grid {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr 1fr;
            gap: 10px;
            margin-bottom: 8px;
          }
          .examination-item {
            display: flex;
            flex-direction: column;
          }
          .examination-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 3px;
            font-size: 9pt;
          }
          .examination-value {
            border-bottom: 1px solid #495057;
            min-height: 20px;
            padding-bottom: 2px;
            color: #212529;
            font-size: 9pt;
          }
          .family-history-box {
            border: 2px solid #333;
            padding: 6px;
            margin-bottom: 8px;
          }
          .family-history-title {
            font-weight: bold;
            font-size: 9.5pt;
            margin-bottom: 6px;
            color: #2c3e50;
          }
          .family-history-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 6px;
          }
          .family-history-table th {
            border: 1px solid #333;
            padding: 4px 6px;
            background-color: #f0f0f0;
            font-weight: bold;
            font-size: 8.5pt;
            text-align: left;
          }
          .family-history-table td {
            border: 1px solid #333;
            padding: 4px 6px;
            font-size: 8.5pt;
            min-height: 20px;
          }
          .physical-exam-section {
            margin-bottom: 8px;
          }
          .test-row {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 6px;
            font-size: 8pt;
          }
          .test-label {
            font-weight: 600;
            color: #495057;
            min-width: 100px;
          }
          .checkbox-group {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            margin-right: 12px;
          }
          .checkbox-label {
            font-size: 9pt;
            color: #495057;
          }
          .two-column-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 8px;
          }
          .body-system-item {
            margin-bottom: 4px;
            font-size: 8pt;
          }
          .body-system-label {
            font-weight: 600;
            color: #495057;
            display: inline-block;
            min-width: 80px;
          }
          .body-system-value {
            border-bottom: 1px solid #495057;
            min-height: 18px;
            display: inline-block;
            min-width: 150px;
            margin-left: 6px;
            padding-bottom: 2px;
            color: #212529;
          }
          .cardiopulmonary-item {
            margin-bottom: 4px;
            font-size: 8pt;
          }
          .cardiopulmonary-label {
            font-weight: 600;
            color: #495057;
            display: inline-block;
            min-width: 100px;
          }
          .cardiopulmonary-value {
            border-bottom: 1px solid #495057;
            min-height: 18px;
            display: inline-block;
            min-width: 120px;
            margin-left: 6px;
            padding-bottom: 2px;
            color: #212529;
          }
          .physician-info {
            margin-top: 8px;
            padding-top: 6px;
          }
          .physician-row {
            display: flex;
            gap: 12px;
            margin-bottom: 6px;
            font-size: 8pt;
          }
          .physician-label {
            font-weight: 600;
            color: #495057;
            min-width: 140px;
          }
          .physician-value {
            border-bottom: 1px solid #495057;
            flex: 1;
            min-height: 18px;
            padding-bottom: 2px;
            color: #212529;
          }
          input[type="checkbox"], input[type="radio"] {
            width: 12px;
            height: 12px;
            margin: 0;
            vertical-align: middle;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
          </div>
          <div class="contact-info">
            <div>Phone: 818-888-7772</div>
            <div>Fax: 818-888-7773</div>
            <div>22151 Ventura Blvd., #201</div>
            <div>Woodland Hills, Ca, 91364</div>
          </div>
          <div class="header-content">
            <h1>Employee Health Examination</h1>
            <p class="subtitle">(To be completed by the employee's physician)</p>
          </div>
        </div>

        <div class="physician-statement">
          <div class="statement-text">
            I have examined <span class="statement-line">${(data.patientTitle || "") + " " + (data.patientName || "________________")}</span> 
            of: <span class="statement-line">${data.positionFull || "________________"}</span> 
            who is applying for the position <span class="statement-line">${data.positionFull || "________________"}</span> 
            and have found no condition that appears to prevent him/her from performing the duties of the position applied for, 
            with the exception or possible exception of the following: 
          </div>
          <div class="statement-blank">${data.conditionException || "None"}</div>
          
          <div class="statement-text" style="margin-top: 6px;">
            I have found no indication of any condition which might represent a possible hazard to the health of the patients 
            or other employees of this facility. He/she is free from communicable diseases.
          </div>
          <div class="statement-blank">${data.noHazardStatement || "None"}</div>
        </div>

        <div class="divider"></div>

        <div class="section-title">EXAMINATION</div>
        <div class="examination-grid">
          <div class="examination-item">
            <div class="examination-label">Height:</div>
            <div class="examination-value">${data.height || "________________"}</div>
          </div>
          <div class="examination-item">
            <div class="examination-label">Weight:</div>
            <div class="examination-value">${data.weight || "________________"}</div>
          </div>
          <div class="examination-item">
            <div class="examination-label">Blood Pressure:</div>
            <div class="examination-value">${data.bloodPressure || "________________"}</div>
          </div>
          <div class="examination-item">
            <div class="examination-label">Pulse:</div>
            <div class="examination-value">${data.pulse || "________________"}</div>
          </div>
        </div>

        <div class="family-history-box">
          <div class="family-history-title">
            Family History: Any significant illness in the family? If so, please state the illness and relationship.
          </div>
          <table class="family-history-table">
            <thead>
              <tr>
                <th>Family Member</th>
                <th>Illness</th>
                <th>Relationship</th>
              </tr>
            </thead>
            <tbody>
              ${data.familyHistory.map(fh => `
                <tr>
                  <td>${fh.member || ""}</td>
                  <td>${fh.illness || ""}</td>
                  <td>${fh.relationship || ""}</td>
                </tr>
              `).join('')}
              ${Array(Math.max(0, 3 - data.familyHistory.length)).fill(0).map(() => `
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="divider"></div>

        <div class="section-title">PHYSICAL EXAMINATION</div>
        
        <div class="physical-exam-section">
          <div class="test-row">
            <span class="test-label">PPD Test:</span>
            <div class="checkbox-group">
              <input type="checkbox" ${data.ppdTestResult === "Negative" ? "checked" : ""} disabled>
              <span class="checkbox-label">Negative</span>
            </div>
            <div class="checkbox-group">
              <input type="checkbox" ${data.ppdTestResult === "Positive" ? "checked" : ""} disabled>
              <span class="checkbox-label">Positive</span>
            </div>
            <span style="margin-left: 10px;">Mantoux: <span class="body-system-value" style="min-width: 100px;">${data.ppdMantoux || "______"}</span></span>
            <span style="margin-left: 10px;">Date Administered: <span class="body-system-value" style="min-width: 100px;">${formatDateShort(data.ppdDateAdministered) || "______"}</span></span>
          </div>
          
          <div class="test-row">
            <span class="test-label">Chest X-Ray (if indicated):</span>
            <div class="checkbox-group">
              <input type="checkbox" ${data.chestXRayResult === "Negative" ? "checked" : ""} disabled>
              <span class="checkbox-label">Negative</span>
            </div>
            <div class="checkbox-group">
              <input type="checkbox" ${data.chestXRayResult === "Positive" ? "checked" : ""} disabled>
              <span class="checkbox-label">Positive</span>
            </div>
            <span style="margin-left: 10px;">Date Administered: <span class="body-system-value" style="min-width: 100px;">${formatDateShort(data.chestXRayDate) || "______"}</span></span>
          </div>
        </div>

        <div class="two-column-grid">
          <div>
            <div class="body-system-item">
              <span class="body-system-label">Adenopathy:</span>
              <span class="body-system-value">${data.adenopathy || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Reflexes:</span>
              <span class="body-system-value">${data.reflexes || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Eyes:</span>
              <span class="body-system-value">${data.eyes || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Hearing:</span>
              <span class="body-system-value">${data.hearing || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Nose:</span>
              <span class="body-system-value">${data.nose || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Throat:</span>
              <span class="body-system-value">${data.throat || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Tongue:</span>
              <span class="body-system-value">${data.tongue || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Teeth:</span>
              <span class="body-system-value">${data.teeth || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Abdomen:</span>
              <span class="body-system-value">${data.abdomen || ""}</span>
            </div>
            <div class="body-system-item">
              <span class="body-system-label">Rectal:</span>
              <span class="body-system-value">${data.rectal || ""}</span>
            </div>
          </div>
          <div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Chest: Breath Sounds:</span>
              <span class="cardiopulmonary-value">${data.chestBreathSounds || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Resonance:</span>
              <span class="cardiopulmonary-value">${data.chestResonance || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Heart: Size:</span>
              <span class="cardiopulmonary-value">${data.heartSize || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Murmur:</span>
              <span class="cardiopulmonary-value">${data.heartMurmur || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Rhythm:</span>
              <span class="cardiopulmonary-value">${data.heartRhythm || ""}</span>
            </div>
            <div class="cardiopulmonary-item">
              <span class="cardiopulmonary-label">Arteries:</span>
              <span class="cardiopulmonary-value">${data.arteries || ""}</span>
            </div>
          </div>
        </div>

        <div class="divider"></div>

        <div class="physician-info">
          <div class="physician-row">
            <span class="physician-label">Physician's Name:</span>
            <span class="physician-value">${data.physicianName || "________________"}</span>
            <span class="physician-label" style="min-width: 160px; margin-left: 15px;">Physician's Phone Number:</span>
            <span class="physician-value">${data.physicianPhone || "________________"}</span>
          </div>
          <div class="physician-row">
            <span class="physician-label">Physician's Address:</span>
            <span class="physician-value">${data.physicianAddress || "________________"}</span>
          </div>
          <div class="physician-row">
            <span class="physician-label">Physician's Signature:</span>
            <span class="physician-value">${data.physicianSignature || "________________"}</span>
            <span class="physician-label" style="min-width: 80px; margin-left: 15px;">Date:</span>
            <span class="physician-value">${formatDateShort(data.physicianDate) || "________________"}</span>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const printHealthForm = (data) => {
    const printHTML = generateHealthPrintFormHTML(data);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printHTML);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Ensure positionFull is set before submitting
    const submitData = {
      ...healthFormData,
      positionFull: healthFormData.positionFull || positionMap[healthFormData.position] || healthFormData.position
    };

    // Save to health history
    const historyEntry = {
      id: Date.now(),
      employeeName: healthFormData.employeeName,
      employeeId: healthFormData.employeeId,
      submittedAt: new Date().toISOString(),
      data: submitData
    };

    const updatedHistory = [historyEntry, ...healthHistory];
    setHealthHistory(updatedHistory);
    localStorage.setItem('healthHistory', JSON.stringify(updatedHistory));

    // Automatically trigger print, then reset form
    setTimeout(() => {
      printHealthForm(submitData);
      resetForm();
      setSelectedPosition("");
      setShowForm(false);
      setShowUserList(true);
    }, 300);
  };

  const resetForm = () => {
    setHealthFormData({
      employeeId: "",
      employeeName: "",
      position: "",
      positionFull: "",
      patientTitle: "",
      patientName: "",
      conditionException: "",
      noHazardStatement: "",
      communicableDiseaseFree: "",
      height: "",
      weight: "",
      bloodPressure: "",
      pulse: "",
      familyHistory: [
        { member: "", illness: "", relationship: "" },
        { member: "", illness: "", relationship: "" },
        { member: "", illness: "", relationship: "" },
      ],
      ppdTestResult: "",
      ppdMantoux: "",
      ppdDateAdministered: "",
      chestXRayResult: "",
      chestXRayDate: "",
      adenopathy: "",
      reflexes: "",
      eyes: "",
      hearing: "",
      nose: "",
      throat: "",
      tongue: "",
      teeth: "",
      abdomen: "",
      rectal: "",
      chestBreathSounds: "",
      chestResonance: "",
      heartSize: "",
      heartMurmur: "",
      heartRhythm: "",
      arteries: "",
      physicianName: "",
      physicianPhone: "",
      physicianAddress: "",
      physicianSignature: "",
      physicianDate: "",
    });
    setSelectedPosition("");
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Employee Health Examination</h1>
        <button
          onClick={() => navigate("/forms")}
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
        >
          Back to Forms
        </button>
      </div>

      {showUserList && !showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Select Employee</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position *
            </label>
            <select
              id="selectedPosition"
              value={selectedPosition}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="">Select a position...</option>
              {positions.map((position) => (
                <option key={position} value={position}>
                  {position}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee Name *
            </label>
            <input
              type="text"
              name="employeeName"
              value={healthFormData.employeeName || ""}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder="Enter employee name"
              required
            />
          </div>
          <button
            onClick={() => {
              if (!selectedPosition) {
                alert("Please select a position");
                return;
              }
              if (!healthFormData.employeeName || healthFormData.employeeName.trim() === "") {
                alert("Please enter employee name");
                return;
              }
              // Auto-populate position full name when showing form
              const positionFull = positionMap[selectedPosition] || selectedPosition;
              setHealthFormData(prev => ({
                ...prev,
                position: selectedPosition,
                positionFull: positionFull
              }));
              setShowUserList(false);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            disabled={!selectedPosition || !healthFormData.employeeName || healthFormData.employeeName.trim() === ""}
          >
            Continue
          </button>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Information Display */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Name *
                  </label>
                  <input
                    type="text"
                    name="employeeName"
                    value={healthFormData.employeeName || ""}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter employee name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position:
                  </label>
                  <p className="text-sm text-gray-900 font-semibold">{healthFormData.positionFull || positionMap[healthFormData.position] || healthFormData.position || selectedPosition || "N/A"}</p>
                </div>
              </div>
            </div>

            {/* Initial Physician's Statement */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Initial Physician's Statement</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient Title:
                </label>
                <select
                  name="patientTitle"
                  value={healthFormData.patientTitle}
                  onChange={handleInputChange}
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">Select...</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient Name:
                </label>
                <input
                  type="text"
                  name="patientName"
                  value={healthFormData.patientName}
                  onChange={handleInputChange}
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition Exception (if any): <span className="text-gray-500 font-normal">(Leave blank or enter "None" if no exceptions)</span>
                </label>
                <textarea
                  name="conditionException"
                  value={healthFormData.conditionException}
                  onChange={handleInputChange}
                  rows="2"
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="None (or describe any exceptions)"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  No Hazard Statement: <span className="text-gray-500 font-normal">(Default: "None" - click to edit if needed)</span>
                </label>
                <textarea
                  name="noHazardStatement"
                  value={healthFormData.noHazardStatement}
                  onChange={handleInputChange}
                  rows="1"
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="None"
                />
              </div>
            </div>

            {/* EXAMINATION Section */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">EXAMINATION</h3>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height:
                  </label>
                  <input
                    type="text"
                    name="height"
                    value={healthFormData.height}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="e.g., 5'8&quot; or 68 inches"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weight:
                  </label>
                  <input
                    type="text"
                    name="weight"
                    value={healthFormData.weight}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="e.g., 150 lbs"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Blood Pressure:
                  </label>
                  <input
                    type="text"
                    name="bloodPressure"
                    value={healthFormData.bloodPressure}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="e.g., 120/80"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pulse:
                  </label>
                  <input
                    type="text"
                    name="pulse"
                    value={healthFormData.pulse}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="e.g., 72 bpm"
                  />
                </div>
              </div>
            </div>

            {/* Family History Section */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Family History</h3>
              <p className="text-sm text-gray-600 mb-4">
                Any significant illness in the family? If so, please state the illness and relationship.
              </p>

              <div className="border-2 border-gray-400 p-4 rounded-lg">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 px-3 py-2 text-left text-sm font-semibold">Family Member</th>
                      <th className="border border-gray-400 px-3 py-2 text-left text-sm font-semibold">Illness</th>
                      <th className="border border-gray-400 px-3 py-2 text-left text-sm font-semibold">Relationship</th>
                    </tr>
                  </thead>
                  <tbody>
                    {healthFormData.familyHistory.map((fh, index) => (
                      <tr key={index}>
                        <td className="border border-gray-400 px-3 py-2">
                          <input
                            type="text"
                            name={`familyMember_${index}`}
                            value={fh.member}
                            onChange={handleInputChange}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          />
                        </td>
                        <td className="border border-gray-400 px-3 py-2">
                          <input
                            type="text"
                            name={`familyIllness_${index}`}
                            value={fh.illness}
                            onChange={handleInputChange}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          />
                        </td>
                        <td className="border border-gray-400 px-3 py-2">
                          <input
                            type="text"
                            name={`familyRelationship_${index}`}
                            value={fh.relationship}
                            onChange={handleInputChange}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* PHYSICAL EXAMINATION Section */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">PHYSICAL EXAMINATION</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PPD Test:
                </label>
                <div className="flex items-center gap-4 mb-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="ppdTestResult"
                      value="Negative"
                      checked={healthFormData.ppdTestResult === "Negative"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Negative
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="ppdTestResult"
                      value="Positive"
                      checked={healthFormData.ppdTestResult === "Positive"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Positive
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mantoux:
                    </label>
                    <input
                      type="text"
                      name="ppdMantoux"
                      value={healthFormData.ppdMantoux}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Administered:
                    </label>
                    <DateInput
                      name="ppdDateAdministered"
                      value={healthFormData.ppdDateAdministered}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chest X-Ray (if indicated):
                </label>
                <div className="flex items-center gap-4 mb-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="chestXRayResult"
                      value="Negative"
                      checked={healthFormData.chestXRayResult === "Negative"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Negative
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="chestXRayResult"
                      value="Positive"
                      checked={healthFormData.chestXRayResult === "Positive"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Positive
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Administered:
                  </label>
                  <DateInput
                    name="chestXRayDate"
                    value={healthFormData.chestXRayDate}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <h4 className="text-md font-semibold text-gray-700">PHYSICAL EXAMINATION - Body Systems</h4>
                  <div className="flex gap-2 ml-auto">
                    <button
                      type="button"
                      onClick={() => fillAllNormal("WNL")}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      Fill All WNL
                    </button>
                    <button
                      type="button"
                      onClick={() => fillAllNormal("Normal")}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Fill All Normal
                    </button>
                    <button
                      type="button"
                      onClick={() => fillAllNormal("")}
                      className="px-4 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors font-medium"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Click "Fill All WNL" or "Fill All Normal" to quickly populate all fields, then edit individual fields as needed.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-semibold text-gray-700 mb-3">General Observations</h4>
                  {[
                    { key: "adenopathy", label: "Adenopathy:" },
                    { key: "reflexes", label: "Reflexes:" },
                    { key: "eyes", label: "Eyes:" },
                    { key: "hearing", label: "Hearing:" },
                    { key: "nose", label: "Nose:" },
                    { key: "throat", label: "Throat:" },
                    { key: "tongue", label: "Tongue:" },
                    { key: "teeth", label: "Teeth:" },
                    { key: "abdomen", label: "Abdomen:" },
                    { key: "rectal", label: "Rectal:" },
                  ].map((item) => (
                    <div key={item.key} className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {item.label}
                      </label>
                      <input
                        type="text"
                        name={item.key}
                        value={healthFormData[item.key]}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="Normal or WNL (if normal)"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="text-md font-semibold text-gray-700 mb-3">Cardiopulmonary</h4>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Chest: Breath Sounds:
                    </label>
                    <input
                      type="text"
                      name="chestBreathSounds"
                      value={healthFormData.chestBreathSounds}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Normal or WNL (if normal)"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Resonance:
                    </label>
                    <input
                      type="text"
                      name="chestResonance"
                      value={healthFormData.chestResonance}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Normal or WNL (if normal)"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Heart: Size:
                    </label>
                    <input
                      type="text"
                      name="heartSize"
                      value={healthFormData.heartSize}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Normal or WNL (if normal)"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Murmur:
                    </label>
                    <input
                      type="text"
                      name="heartMurmur"
                      value={healthFormData.heartMurmur}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="None (if normal)"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rhythm:
                    </label>
                    <input
                      type="text"
                      name="heartRhythm"
                      value={healthFormData.heartRhythm}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Regular (if normal)"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Arteries:
                    </label>
                    <input
                      type="text"
                      name="arteries"
                      value={healthFormData.arteries}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Normal or WNL (if normal)"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Physician's Information Section */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Physician's Information</h3>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Physician's Name:
                  </label>
                  <input
                    type="text"
                    name="physicianName"
                    value={healthFormData.physicianName}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Physician's Phone Number:
                  </label>
                  <input
                    type="text"
                    name="physicianPhone"
                    value={healthFormData.physicianPhone}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Physician's Address:
                </label>
                <input
                  type="text"
                  name="physicianAddress"
                  value={healthFormData.physicianAddress}
                  onChange={handleInputChange}
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Physician's Signature:
                  </label>
                  <input
                    type="text"
                    name="physicianSignature"
                    value={healthFormData.physicianSignature}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date:
                  </label>
                  <DateInput
                    name="physicianDate"
                    value={healthFormData.physicianDate}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t border-gray-300">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
              >
                Submit/Print
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setShowUserList(true);
                  resetForm();
                  setSelectedPosition("");
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EmployeeHealthExamination;

